import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Globe, Linkedin, Building2, Loader2, Sparkles, Users, CheckCircle2 } from "lucide-react";

interface AILeadDiscoveryStepProps {
  productName: string;
  industry: string;
  onIndustryChange: (industry: string) => void;
  targetMarket: string;
  onTargetMarketChange: (market: string) => void;
  keywords: string;
  onKeywordsChange: (keywords: string) => void;
  sources: {
    websites: boolean;
    linkedin: boolean;
    directories: boolean;
  };
  onToggleSource: (source: 'websites' | 'linkedin' | 'directories') => void;
  isDiscovering: boolean;
  onDiscover: () => void;
  discoveredCount: number;
  error?: string;
}

export function AILeadDiscoveryStep({
  productName,
  industry,
  onIndustryChange,
  targetMarket,
  onTargetMarketChange,
  keywords,
  onKeywordsChange,
  sources,
  onToggleSource,
  isDiscovering,
  onDiscover,
  discoveredCount,
  error,
}: AILeadDiscoveryStepProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg">
        <Sparkles className="h-5 w-5 text-primary" />
        <p className="text-sm">
          AI will discover potential leads for <span className="font-semibold">{productName || 'your product'}</span> based on your criteria
        </p>
      </div>

      {error && (
        <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="grid gap-4">
        <div className="space-y-2">
          <Label htmlFor="industry">Target Industry</Label>
          <Input
            id="industry"
            placeholder="e.g., Technology, Healthcare, Finance"
            value={industry}
            onChange={(e) => onIndustryChange(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="targetMarket">Target Market / Region</Label>
          <Input
            id="targetMarket"
            placeholder="e.g., SMBs in North America, Enterprise EMEA"
            value={targetMarket}
            onChange={(e) => onTargetMarketChange(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="keywords">Keywords (comma separated)</Label>
          <Input
            id="keywords"
            placeholder="e.g., SaaS, B2B, cloud solutions"
            value={keywords}
            onChange={(e) => onKeywordsChange(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Discovery Sources</Label>
        <div className="grid grid-cols-3 gap-3">
          <Card 
            className={`p-3 cursor-pointer transition-all ${sources.websites ? 'border-primary bg-primary/5' : 'hover:border-primary/50'}`}
            onClick={() => onToggleSource('websites')}
          >
            <div className="flex items-center gap-2">
              <Checkbox checked={sources.websites} />
              <Globe className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Websites</span>
            </div>
          </Card>
          <Card 
            className={`p-3 cursor-pointer transition-all ${sources.linkedin ? 'border-primary bg-primary/5' : 'hover:border-primary/50'}`}
            onClick={() => onToggleSource('linkedin')}
          >
            <div className="flex items-center gap-2">
              <Checkbox checked={sources.linkedin} />
              <Linkedin className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">LinkedIn</span>
            </div>
          </Card>
          <Card 
            className={`p-3 cursor-pointer transition-all ${sources.directories ? 'border-primary bg-primary/5' : 'hover:border-primary/50'}`}
            onClick={() => onToggleSource('directories')}
          >
            <div className="flex items-center gap-2">
              <Checkbox checked={sources.directories} />
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Directories</span>
            </div>
          </Card>
        </div>
      </div>

      <Button 
        onClick={onDiscover} 
        disabled={isDiscovering || !industry}
        className="w-full"
      >
        {isDiscovering ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Discovering Leads...
          </>
        ) : (
          <>
            <Search className="h-4 w-4 mr-2" />
            Discover Leads with AI
          </>
        )}
      </Button>

      {discoveredCount > 0 && (
        <div className="flex items-center gap-2 p-3 bg-green-500/10 text-green-600 rounded-lg">
          <CheckCircle2 className="h-5 w-5" />
          <span className="text-sm font-medium">
            Discovered {discoveredCount} potential leads
          </span>
          <Badge variant="secondary" className="ml-auto">
            <Users className="h-3 w-3 mr-1" />
            Ready for scoring
          </Badge>
        </div>
      )}
    </div>
  );
}
